import React, { useRef, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  FormGroup,
  Button,
  Paper,
  Chip,
  Divider,
  Autocomplete,
  CircularProgress
} from '@mui/material';
import axios from 'axios';
import { APIURL } from './../../config';

import { useReactToPrint } from 'react-to-print';

// Section wrapper for consistent styled blocks
const Section = ({ number, title, children }) => (
  <Paper variant="outlined" sx={{ p: 3, mb: 4, borderRadius: 2 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
      {number !== undefined && <Chip label={number} color="primary" size="small" sx={{ fontWeight: 600 }} />}
      {title && <Typography variant="h6" sx={{ fontWeight: 600 }}>{title}</Typography>}
    </Box>
    <Divider sx={{ mb: 2 }} />
    {children}
  </Paper>
);

const SocialCareReferralForm = ({ form, onChange, onSubmit, disableSubmit }) => {
  const contentRef  = useRef(null);
  const handlePrint = useReactToPrint({
    contentRef 
  });
  const [addrOpts, setAddrOpts] = useState([]);
  const [addrLoading, setAddrLoading] = useState(false);

  const fetchAddressSuggestions = useCallback(async (input) => {
    if (!input || input.trim().length < 3) { setAddrOpts([]); return; }
    setAddrLoading(true);
    try {
      const resp = await axios.get(`${APIURL}/maps`, { params: { input } });
      console.log(resp.data);
      setAddrOpts(resp.data?.predictions || []);
    } catch { setAddrOpts([]); } finally { setAddrLoading(false); }
  }, [APIURL]);

  const parseComponent = (components, type, short=false) => {
    const c = components.find(comp => comp?.types?.includes(type));
    if (!c) return '';
    return (short ? c.short_name : c.long_name) || '';
  };

  const handleSelectAddress = useCallback(async (option) => {
    if (!option) return;
    try {
      const resp = await axios.get(`${APIURL}/maps/details`, { params: { place_id: option.place_id } });
      const result = resp.data?.result;
      if (!result?.address_components) return;
      const comps = result.address_components;
      const streetNum = parseComponent(comps, 'street_number');
      const route = parseComponent(comps, 'route');
      const subpremise = parseComponent(comps, 'subpremise');
      const city = ['locality', 'postal_town', 'sublocality', 'sublocality_level_1', 'sublocality_level_2']
        .map(type => parseComponent(comps, type))
        .find(Boolean) || '';
      const state = parseComponent(comps, 'administrative_area_level_1', true);
      const postcode = parseComponent(comps, 'postal_code');
      const street = [streetNum, route].filter(Boolean).join(' ');
      const formattedAddress = [street, subpremise, city, state, postcode].filter(Boolean).join(', ');
      if (formattedAddress) onChange({ target: { name: 'address', value: formattedAddress } });
    } catch {}
  }, [APIURL, onChange]);

  // Shared phone helpers
  const sanitizePhoneDigits = (value = '') => {
    let digits = (value || '').replace(/\D/g, '');
    if (digits.startsWith('1')) digits = digits.slice(0, 11); else digits = digits.slice(0, 10);
    return digits;
  };

  const formatPhoneProgressive = (digits = '') => {
    if (!digits) return '';
    let d = digits;
    let prefix = '';
    if (d.startsWith('1')) { prefix = '+1 '; d = d.slice(1); }
    if (d.length <= 3) return `${prefix}(${d}`;
    if (d.length <= 6) return `${prefix}(${d.slice(0,3)}) ${d.slice(3)}`;
    return `${prefix}(${d.slice(0,3)}) ${d.slice(3,6)}-${d.slice(6)}`;
  };

  const isCompletePhone = (digits = '') => (
    digits.length === 10 || (digits.length === 11 && digits.startsWith('1'))
  );


  return (
    <Box component="form" ref={contentRef} onSubmit={onSubmit} sx={{ maxWidth: 880, mx: 'auto', p: 2, pb: 6 }}>
      <Box sx={{ textAlign: 'center', mb: 5 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>Referral Form</Typography>
        <Typography variant="subtitle1" color="text.secondary">Complete the sections below. Provide as much detail as possible.</Typography>
      </Box>

      {/* 1. Your information */}
      <Section number={1} title="Your information">
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <TextField fullWidth label="Your name" name="submitterName" value={form.submitterName||''} onChange={onChange} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <TextField
              fullWidth
              label="Your contact number"
              name="submitterContact"
              value={formatPhoneProgressive(form.submitterContact || '')}
              error={!!form.submitterContact && !isCompletePhone(form.submitterContact)}
              onChange={(e)=>{
                const digits = sanitizePhoneDigits(e.target.value);
                onChange({ target: { name: 'submitterContact', value: digits } });
              }}
              inputProps={{ inputMode: 'tel' }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <TextField fullWidth label="Your Email" type="email" name="submitterEmail" value={form.submitterEmail||''} onChange={onChange} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <TextField fullWidth label="Organization/Role" name="organization" value={form.organization||''} onChange={onChange} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <FormControl fullWidth>
            <InputLabel>Relationship to Client</InputLabel>
            <Select sx={{ width: '100%' }}
               name="relationship"
               value={form.relationship}
               onChange={onChange}
             >
              <MenuItem value="self">Self</MenuItem>
              <MenuItem value="family">Family Member</MenuItem>
              <MenuItem value="social_worker">Social Worker</MenuItem>
              <MenuItem value="gp">GP</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </Select>
          </FormControl>
        </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <TextField
            fullWidth
            label="Date of Referral"
            name="referralDate"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={form.referralDate}
            onChange={onChange}
          />
        </Grid>
        </Grid>
      </Section>

      {/* 2. Client/Service User Details */}
      <Section number={2} title="Client / Service User Details">
        <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <TextField fullWidth label="Full Name" name="clientName" value={form.clientName} onChange={onChange} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <TextField fullWidth label="Date of Birth" name="dob" type="date" InputLabelProps={{ shrink: true }} value={form.dob} onChange={onChange} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <FormControl fullWidth>
            <InputLabel>Gender</InputLabel>
            <Select sx={{ width: '100%' }}
               name="gender"
               value={form.gender}
               onChange={onChange}
             >
              <MenuItem value="male">Male</MenuItem>
              <MenuItem value="female">Female</MenuItem>
              <MenuItem value="non-binary">Non-Binary</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid size={12}>
          <Autocomplete
            fullWidth
            options={addrOpts}
            loading={addrLoading}
            getOptionLabel={(o) => o?.description || ''}
            value={null}
            inputValue={form.address || ''}
            onInputChange={(e, value, reason) => {
              if (reason === 'input') {
                onChange({ target: { name: 'address', value } });
                fetchAddressSuggestions(value);
              }
            }}
            onChange={(e, val) => handleSelectAddress(val)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Full Address"
                helperText="Street, unit, city, state and postal code"
                fullWidth
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {addrLoading ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <TextField
            fullWidth
            label="Contact Number"
            name="clientContact"
            value={formatPhoneProgressive(form.clientContact || '')}
            error={!!form.clientContact && !isCompletePhone(form.clientContact)}
            onChange={(e)=>{
              const digits = sanitizePhoneDigits(e.target.value);
              onChange({ target: { name: 'clientContact', value: digits } });
            }}
            inputProps={{ inputMode: 'tel' }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <TextField fullWidth label="Email Address" name="clientEmail" type="email" value={form.clientEmail} onChange={onChange} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <TextField fullWidth label="Preferred Language" name="language" value={form.language} onChange={onChange} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <FormControl component="fieldset">
            <RadioGroup name="interpreter" row value={form.interpreter} onChange={onChange}>
              <FormControlLabel value="yes" control={<Radio />} label="Interpreter Required: Yes" />
              <FormControlLabel value="no" control={<Radio />} label="Interpreter Required: No" />
            </RadioGroup>
          </FormControl>
        </Grid>
        {form.interpreter === 'yes' && (
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <TextField fullWidth label="Interpreter Language" name="interpreterLanguage" value={form.interpreterLanguage} onChange={onChange} />
          </Grid>
        )}
        <Grid size={12}>
          <TextField fullWidth multiline rows={3} label="Communication Needs" name="communicationNeeds" value={form.communicationNeeds} onChange={onChange} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <TextField fullWidth label="Nationality" name="nationality" value={form.nationality} onChange={onChange} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <TextField fullWidth label="Immigration Status" name="immigrationStatus" value={form.immigrationStatus} onChange={onChange} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <TextField fullWidth label="Emergency Contact Name" name="emergencyContactName" value={form.emergencyContactName} onChange={onChange} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <TextField
            fullWidth
            label="Emergency Contact Number"
            name="emergencyContactNumber"
            value={formatPhoneProgressive(form.emergencyContactNumber || '')}
            error={!!form.emergencyContactNumber && !isCompletePhone(form.emergencyContactNumber)}
            onChange={(e)=>{
              const digits = sanitizePhoneDigits(e.target.value);
              onChange({ target: { name: 'emergencyContactNumber', value: digits } });
            }}
            inputProps={{ inputMode: 'tel' }}
          />
        </Grid>
        </Grid>
      </Section>

      {/* 3. Reason for Referral */}
      <Section number={3} title="Reason for Referral">
        <Grid container spacing={2}>
        <Grid size={12}>
          <FormControl component="fieldset">
            <FormGroup>
              {['mental_health','physical_disability','housing','child_protection','adult_safeguarding','substance_misuse','domestic_abuse'].map(val => (
                <FormControlLabel
                  key={val}
                  control={<Checkbox
                    name="reasons"
                    value={val}
                    checked={form?.reasons?.includes(val)}
                    onChange={e => {
                      const { checked, value } = e.target;
                      const newReasons = checked
                        ? [...form.reasons, value]
                        : form.reasons.filter(r => r !== value);
                      onChange({ target: { name: 'reasons', value: newReasons } });
                    }}
                  />}
                  label={val.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                />
              ))}
              <Box sx={{ display:'flex', alignItems:'center', mt:1 }}>
                <Checkbox
                  name="reasons"
                  value="other"
                  checked={form?.reasons?.includes('other')}
                  onChange={e => {
                    const { checked } = e.target;
                    const newReasons = checked
                      ? [...form.reasons, 'other']
                      : form.reasons.filter(r => r !== 'other');
                    onChange({ target: { name: 'reasons', value: newReasons } });
                  }}
                />
                <TextField placeholder="Other reason" name="reasonOther" value={form.reasonOther||''} onChange={onChange} sx={{ ml:1 }} />
              </Box>
            </FormGroup>
          </FormControl>
        </Grid>
  <Grid size={12}>
          <TextField fullWidth multiline rows={4} label="Description of Need" name="description" value={form.description} onChange={onChange} />
        </Grid>
  <Grid size={{ xs: 12, md: 6 }}>
          <FormControl fullWidth>
            <InputLabel>Urgency</InputLabel>
            <Select sx={{ width: '100%' }}
               name="urgency"
               value={form.urgency}
               onChange={onChange}
             >
              <MenuItem value="immediate">Immediate (within 24 hours)</MenuItem>
              <MenuItem value="urgent">Urgent (within 48 hours)</MenuItem>
              <MenuItem value="standard">Standard (within 5 working days)</MenuItem>
              <MenuItem value="non-urgent">Non-Urgent</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        </Grid>
      </Section>

      {/* 4. Background Information */}
      <Section number={4} title="Background Information">
        <Grid container spacing={2}>
        <Grid size={12}>
          <TextField fullWidth multiline rows={3} label="Medical History" name="medicalHistory" value={form.medicalHistory} onChange={onChange} />
        </Grid>
        <Grid size={12}>
          <TextField fullWidth multiline rows={3} label="Current or Past Services Involved" name="servicesInvolved" value={form.servicesInvolved} onChange={onChange} />
        </Grid>
        <Grid size={12}>
          <TextField fullWidth multiline rows={3} label="Family/Household Composition" name="householdComposition" value={form.householdComposition} onChange={onChange} />
        </Grid>
        <Grid size={12}>
          <TextField fullWidth multiline rows={3} label="Social/Environmental Factors" name="socialFactors" value={form.socialFactors} onChange={onChange} />
        </Grid>
        </Grid>
      </Section>

      {/* 5. Risk Assessment */}
      <Section number={5} title="Risk Assessment">
        <Grid container spacing={2}>
        <Grid size={12}>
          <FormControl component="fieldset">
            <RadioGroup name="immediateRisks" row value={form.immediateRisks} onChange={onChange}>
              <FormControlLabel value="yes" control={<Radio />} label="Immediate Risks: Yes" />
              <FormControlLabel value="no" control={<Radio />} label="Immediate Risks: No" />
            </RadioGroup>
          </FormControl>
        </Grid>
        <Grid size={12}>
          <TextField fullWidth multiline rows={3} label="If Yes, Specify Risks" name="risksDetails" value={form.risksDetails} onChange={onChange} />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField fullWidth multiline rows={2} label="Known Vulnerabilities" name="vulnerabilities" value={form.vulnerabilities} onChange={onChange} />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField fullWidth multiline rows={2} label="Risks to Staff or Others" name="staffRisks" value={form.staffRisks} onChange={onChange} />
        </Grid>
        <Grid size={12}>
          <FormControl component="fieldset">
            <RadioGroup name="safeguarding" row value={form.safeguarding} onChange={onChange}>
              <FormControlLabel value="yes" control={<Radio />} label="Safeguarding Concern: Yes" />
              <FormControlLabel value="no" control={<Radio />} label="Safeguarding Concern: No" />
            </RadioGroup>
          </FormControl>
        </Grid>
        {form.safeguarding === 'yes' && (
          <Grid size={12}>
            <TextField fullWidth multiline rows={3} label="If Yes, Provide Details" name="safeguardingDetails" value={form.safeguardingDetails} onChange={onChange} />
          </Grid>
        )}
        </Grid>
      </Section>

      {/* 6. Consent and Legal Information */}
      <Section number={6} title="Consent & Legal Information">
        <Grid container spacing={2}>
        <Grid size={12}>
          <FormControl component="fieldset">
            <RadioGroup name="consent" row value={form.consent} onChange={onChange}>
              <FormControlLabel value="yes" control={<Radio />} label="Client Consented" />
              <FormControlLabel value="no" control={<Radio />} label="Not Consented" />
              <FormControlLabel value="not_applicable" control={<Radio />} label="Not Applicable" />
            </RadioGroup>
          </FormControl>
        </Grid>
        {form.consent === 'no' && (
          <Grid size={12}>
            <TextField fullWidth multiline rows={2} label="If No, Explain Why" name="consentExplanation" value={form.consentExplanation} onChange={onChange} />
          </Grid>
        )}
        <Grid size={{ xs: 12, md: 6 }}>
          <FormControl component="fieldset">
            <RadioGroup name="mentalCapacity" row value={form.mentalCapacity} onChange={onChange}>
              <FormControlLabel value="yes" control={<Radio />} label="Has Capacity" />
              <FormControlLabel value="no" control={<Radio />} label="No Capacity" />
              <FormControlLabel value="unknown" control={<Radio />} label="Unknown" />
            </RadioGroup>
          </FormControl>
        </Grid>
        {form.mentalCapacity === 'no' && (
          <Grid size={12}>
            <TextField fullWidth multiline rows={2} label="If No, Provide Details" name="capacityDetails" value={form.capacityDetails} onChange={onChange} />
          </Grid>
        )}
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField fullWidth multiline rows={2} label="Legal Orders" name="legalOrders" value={form.legalOrders} onChange={onChange} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField fullWidth multiline rows={2} label="Other Legal Considerations" name="legalConsiderations" value={form.legalConsiderations} onChange={onChange} />
        </Grid>
        </Grid>
      </Section>

      {/* 7. Additional Information */}
      <Section number={7} title="Additional Information">
        <Grid container spacing={2}>
        <Grid size={12}>
          <TextField fullWidth multiline rows={2} label="Cultural/Religious Considerations" name="culturalConsiderations" value={form.culturalConsiderations} onChange={onChange} />
        </Grid>
        {[1,2].map(i => (
          <React.Fragment key={i}>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField fullWidth label={`Professional ${i} Name`} name={`professionalName_${i}`} value={form[`professionalName_${i}`]||''} onChange={onChange} />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField fullWidth label={`Professional ${i} Role`} name={`professionalRole_${i}`} value={form[`professionalRole_${i}`]||''} onChange={onChange} />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField fullWidth label={`Professional ${i} Contact`} name={`professionalContact_${i}`} value={form[`professionalContact_${i}`]||''} onChange={onChange} />
            </Grid>
          </React.Fragment>
        ))}
        <Grid size={12}>
          <FormControl component="fieldset">
            <RadioGroup name="documentsAttached" row value={form.documentsAttached ? 'yes' : 'no'} onChange={e => onChange({ target: { name: 'documentsAttached', type: 'checkbox', checked: e.target.value === 'yes' } })}>
              <FormControlLabel value="yes" control={<Radio />} label="Supporting Documents Attached: Yes" />
              <FormControlLabel value="no" control={<Radio />} label="No" />
            </RadioGroup>
          </FormControl>
        </Grid>
        {form.documentsAttached && (
          <Grid size={12}>
            <TextField fullWidth multiline rows={2} label="List Documents" name="documentsList" value={form.documentsList} onChange={onChange} />
          </Grid>
        )}
        </Grid>
      </Section>

      {/* 8. Action Requested */}
      <Section number={8} title="Action Requested">
        <Grid container spacing={2}>
   <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <TextField fullWidth multiline rows={3} label="Specific Services Requested" name="servicesRequested" value={form.servicesRequested} onChange={onChange} />
        </Grid>
   <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <TextField fullWidth label="Preferred Timeframe for Response" name="timeframe" value={form.timeframe} onChange={onChange} />
        </Grid>
   <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <TextField fullWidth multiline rows={2} label="Additional Notes or Comments" name="additionalNotes" value={form.additionalNotes} onChange={onChange} />
        </Grid>
        </Grid>
      </Section>

      {/* Declaration */}
      <Section number={9} title="Declaration">
        <Typography variant="body2" paragraph>
          I confirm that the information provided is accurate to the best of my knowledge and that I have adhered to relevant data protection regulations (e.g., GDPR). I understand that this information will be used to assess and provide appropriate services.
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField fullWidth label="Referrer Signature" name="signature" value={form.signature} onChange={onChange} />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField fullWidth label="Date" name="signatureDate" type="date" InputLabelProps={{ shrink: true }} value={form.signatureDate} onChange={onChange} />
          </Grid>
        </Grid>
      </Section>

      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2 }}>
        <Button variant="contained" color="primary" type="submit" disabled={!!disableSubmit}>
          Submit Referral
        </Button>
        <Button variant="outlined" onClick={handlePrint}>
          Print Referral
        </Button>
      </Box>
    </Box>
  );
};

export default SocialCareReferralForm;
